import React, { useEffect, useState, useCallback } from "react";
import { SlInput, SlIcon } from "@shoelace-style/shoelace/dist/react";
import "./CustomReports.css";
import "../../creatives/CreativeList.scss";
import { useDispatch } from "react-redux";
import Datepicker from "react-tailwindcss-datepicker";
import { SlButton } from "@shoelace-style/shoelace/dist/react";

import API from "../../../services/api";
import { Toast } from "../../../shared/Toast";
import CustomReportCreation from "./CustomReportCreation";
import NoDataFound from "../../../shared/NoDataFound";
import TableLoader from "../../../shared/TableLoader";
import { formatDateDDMMYYY } from "../../../utils/dateFormat";
import {
  adPositions,
  BrandsTypes,
  customReportDimensions,
  customReportMetrics,
  teams,
} from "../../../utils/constants";
import CustomReportGeneration from "./CustomReportGeneration";
import config from "../../../config";
import Paginator from "../../../shared/Paginator";
import { debounce } from "../../../utils/Debounce";

function CustomReports() {
  const dispatch = useDispatch();

  const [data, setData] = useState([]);
  const [loader, setLoader] = useState(false);
  const [page, setPage] = useState(0);
  const [showReportCreation, setShowReportCreation] = useState({ view: false });
  const [showReportGeneration, setShowReportGeneration] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [search, setSearch] = useState("");

  //  Fetch reports with useCallback (to avoid recreating function on each render)
  const fetchReports = useCallback(async () => {
    setLoader(true);
    try {
      const res = await API({
        url: "/custom_reports/get-reports",
        method: "POST",
        responseType: "json",
        data: {
          page: currentPage,
          limit: config.PER_PAGE,
          search: search || "",
        },
      });

      if (res.code === 200) {
        setData(res.data.reports || []);
        setTotalPages(Math.ceil(parseInt(res.data.total) / config.PER_PAGE));
        setCurrentPage(currentPage);
      } else {
        Toast("error", res.message || "Failed to fetch reports");
      }
    } catch (error) {
      console.error("Error fetching custom reports:", error);
      Toast(
        "error",
        error.message || "Something went wrong while fetching reports"
      );
    } finally {
      setLoader(false);
    }
  }, [currentPage, search]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  // Render a single row (cleaner)
  const renderReportRow = (item) => (
    <tr key={item.id || item.report_name}>
      <td className="cl-name" data-label="Report Name">{item.report_name}</td>
      <td data-label="Start Date">{formatDateDDMMYYY(item.start_date)}</td>
      <td data-label="End Date">{formatDateDDMMYYY(item.end_date)}</td>
      <td className="cl-cell-wrap" data-label="Selected Filters">
        <div className="cl-clamp">{displaySelectedFilters(item.selected_filters, item)}</div>
      </td>
      <td className="cl-cell-wrap" data-label="Dimensions">
        <div className="cl-clamp">
          {customReportDimensions
            .filter((tempitem) => item.dimensions?.includes(tempitem.value))
            ?.map((item) => item.label)
            .join(", ") || "-"}
        </div>
      </td>
      <td className="cl-cell-wrap" data-label="Metrics">
        <div className="cl-clamp">
          {customReportMetrics
            .filter((tempitem) => item.metrics?.includes(tempitem.value))
            ?.map((item) => item.label)
            .join(", ") || "-"}
        </div>
      </td>
      <td data-label="Actions">
        <div className="d-flex gap-2">
          <SlButton
            type="button"
            variant="primary"
            size="small"
            onClick={() =>
              setShowReportGeneration({
                start_date: new Date(item.start_date * 1000),
                end_date: new Date(item.end_date * 1000),
                selected_filters: item.selected_filters,
                dimensions: item.dimensions,
                metrics: item.metrics,
                report_name: item.report_name,
              })
            }
          >
            Run Report
          </SlButton>
          <SlButton
            type="button"
            variant="primary"
            size="small"
            onClick={() =>
              setShowReportCreation({ view: true, editData: item })
            }
          >
            Edit
          </SlButton>
        </div>
      </td>
    </tr>
  );

  const displaySelectedFilters = (filters, actualData) => {
    if (!filters || Object.keys(filters).length === 0) return "-";
    const filterItems = [];

    Object.entries(filters).forEach(([key, value]) => {
      if (!value || value.length === 0) return;

      let label = "";

      if (key === "new_user_type") {
        label = `Sales Type: ${
          [{ label: "Main", value: -1 }, ...BrandsTypes]
            .filter((item) => value.includes(item.value))
            ?.map((item) => item.label)
            .join(", ") || ""
        }`;
      } else if (key === "position") {
        label = `Position: ${adPositions.join(", ") || ""}`;
      } else if (key === "team") {
        label = `Team: ${
          teams
            .filter((item) => value.includes(item.value))
            ?.map((item) => item.label)
            .join(", ") || ""
        }`;
      } else if (key === "brand_id") {
        label = `Brand: ${
          actualData.brand_options
            ?.filter((item) => value.includes(item.value))
            ?.map((item) => item.label)
            .join(", ") || ""
        }`;
      } else if (key === "order_id") {
        label = `Order: ${
          actualData.order_options
            ?.filter((item) => value.includes(item.value))
            ?.map((item) => item.label)
            .join(", ") || ""
        }`;
      }

      if (label) {
        filterItems.push(
          <div key={key} style={{ marginBottom: "4px" }}>
            {label}
          </div>
        );
      }
    });

    return filterItems.length > 0 ? filterItems : "-";
  };

  // Helper to format objects or arrays into readable strings
  const formatReadable = (val) => {
    if (!val) return "-";
    return Array.isArray(val)
      ? val.join(", ")
      : typeof val === "object"
      ? Object.entries(val)
          .map(([k, v]) => `${k}: ${v}`)
          .join(", ")
      : val.toString();
  };

  if (showReportCreation?.view) {
    return (
      <CustomReportCreation
        onBack={(shouldRefresh = false) => {
          setShowReportCreation(null);
          if (shouldRefresh) fetchReports();
        }}
        editData={showReportCreation?.editData || null}
      />
    );
  }

  if (showReportGeneration) {
    return (
      <CustomReportGeneration
        onBack={() => setShowReportGeneration(false)}
        APIbody={showReportGeneration}
        report_name={showReportGeneration.report_name}
      />
    );
  }

  const handlePageClick = (event) => {
    setCurrentPage(event.selected);
  };

  const searchData = e => {
    setSearch(e.target.value);
    setCurrentPage(0);
    // loadData(e.target.value, 0);
}

  return (
    <div className="creativeListPage">
      <div className="cl-card">
        <header className="cl-header">
          <div className="cl-title-block">
            <h1 className="cl-title">Custom Reports</h1>
            <p className="cl-subtitle">Build, save and run tailored performance reports</p>
          </div>
          <div className="cl-toolbar cl-toolbar--inline">
            <SlInput
              className="cl-search"
              type="text"
              placeholder="Search"
              value={search}
              onSlInput={debounce(searchData, 500)}
            >
              <SlIcon library="lucide" name="search" slot="prefix"></SlIcon>
            </SlInput>
            <SlButton
              type="button"
              variant="primary"
              size="medium"
              onClick={() => setShowReportCreation({ view: true })}
            >
              <SlIcon library="lucide" name="plus" slot="prefix"></SlIcon>
              Add Report
            </SlButton>
          </div>
        </header>

        <div className="cl-table-wrap">
          <table className="cl-table cl-table--multi">
            <thead>
              <tr>
                <th>Report Name</th>
                <th>Start Date</th>
                <th>End Date</th>
                <th>Selected Filters</th>
                <th>Dimensions</th>
                <th>Metrics</th>
                <th>Actions</th>
              </tr>
            </thead>

            {data?.length && !loader ? (
              <tbody>{data.map(renderReportRow)}</tbody>
            ) : !data?.length && !loader ? (
              <NoDataFound colspan="7" />
            ) : (
              <TableLoader colspan="7" />
            )}
          </table>
        </div>
      </div>

      {totalPages > 1 && (
        <div className="cl-pagination">
          <Paginator
            pageCount={totalPages}
            handlePageClick={handlePageClick}
            forcePage={currentPage}
          />
        </div>
      )}
    </div>
  );
}

export default CustomReports;
